/**
 * 회사 용어집 — 팝업 둘(2026-10-23 · 시안 G 2판 G2-3 ~ G2-5 ＋ 평 PG2).
 *
 *   · 용어 추가 / 고치기 — 하나. 추가에는 «붙일 자리»와 미리 보기 한 줄(평 PG2 「하나 넣기에도 요약」)
 *   · 여러 개 넣기 — 한 줄에 하나 · 「용어 | 메모」 · 미리 보기(들어가는 것 · 밀려나는 것 · 건너뛴 것 · 뺀 것)
 *
 * ⛔ 붙일 자리는 «처음에 아무것도 안 골라져 있다» — 사용자 답 「넣을 때 고르기」(기본값 없음).
 *    고르기 전에는 넣기 단추가 잠기고, 미리 보기도 «고르면 보여 준다»고만 한다.
 * ★ 미리 보기는 서버의 dry_run — 실제 넣기와 «같은 계획 함수»라 보인 것과 다르게 들어가지 않는다.
 */
import { useEffect, useRef, useState } from 'react';
import { X } from '../../../icons';
import {
  addGlossaryBulk, addGlossaryTerm, patchGlossaryTerm,
  type GlossaryPlace, type GlossaryPlan, type GlossaryTerm, type GlossaryView,
} from '../../../api/voiceGlossary';
import { errText } from './ExternalApiEditModal';
import { addSummary, bulkSummary, type SummaryLine } from './glossaryView';

/** 미리 보기를 부를 때까지 기다리는 시간 — 칠 때마다 부르지 않는다. */
export const GLOSSARY_PREVIEW_DELAY_MS = 300;

function Summary({ lines }: { lines: SummaryLine[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="gl-pv" role="status" aria-live="polite">
      {lines.map((l) => <span key={l.text} className={`gl-pv__l gl-pv__l--${l.tone}`}>{l.text}</span>)}
    </div>
  );
}

function PlaceRadio({ name, value, onChange }: { name: string; value: GlossaryPlace | null; onChange: (p: GlossaryPlace) => void }) {
  return (
    <fieldset className="cl-field gl-place">
      <legend>붙일 자리 <em aria-hidden>*</em></legend>
      <label className="gl-place__opt">
        <input type="radio" name={name} checked={value === 'top'} onChange={() => onChange('top')} />
        <span><b>맨 위</b> — 바로 힌트에 들어감</span>
      </label>
      <label className="gl-place__opt">
        <input type="radio" name={name} checked={value === 'bottom'} onChange={() => onChange('bottom')} />
        <span><b>맨 아래</b> — 지금 차례를 그대로 둠</span>
      </label>
    </fieldset>
  );
}

/** 칠 때마다가 아니라 멈춘 뒤 한 번 · 늦게 온 옛 답은 버린다. */
function usePreview<T>(key: string | null, load: () => Promise<T>): { value: T | null; busy: boolean; error: string | null } {
  const [value, setValue] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const my = ++seq.current;
    setValue(null); setError(null);
    if (key === null) { setBusy(false); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      loadRef.current()
        .then((v) => { if (seq.current === my) setValue(v); })
        .catch((e: unknown) => { if (seq.current === my) setError(errText(e, '미리 보기를 불러오지 못했습니다')); })
        .finally(() => { if (seq.current === my) setBusy(false); });
    }, GLOSSARY_PREVIEW_DELAY_MS);
    return () => clearTimeout(t);
  }, [key]);
  return { value, busy, error };
}

interface TermModalProps {
  inst: string;
  /** null 이면 새로 넣기. */
  term: GlossaryTerm | null;
  termMax: number;
  memoMax: number;
  maxTerms: number;
  onClose: () => void;
  onSaved: (view: GlossaryView, message: string) => void;
}

export function GlossaryTermModal({ inst, term, termMax, memoMax, maxTerms, onClose, onSaved }: TermModalProps) {
  const isNew = term === null;
  const [text, setText] = useState(term?.term ?? '');
  const [memo, setMemo] = useState(term?.memo ?? '');
  const [enabled, setEnabled] = useState(term?.enabled ?? true);
  const [place, setPlace] = useState<GlossaryPlace | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = text.trim();
  const m = memo.trim();
  const termOk = t.length >= 1 && t.length <= termMax;
  const memoOk = m.length <= memoMax;
  const previewKey = isNew && termOk && memoOk && place ? JSON.stringify([t, m, enabled, place]) : null;
  const preview = usePreview<GlossaryPlan>(previewKey, async () => (await addGlossaryTerm(inst, {
    term: t, memo: m === '' ? null : m, enabled, place: place!, dry_run: true,
  })).plan);
  const plan = preview.value;
  const blocked = plan !== null && (plan.added === 0 || plan.over_limit);
  const canSave = termOk && memoOk && !busy && (isNew ? place !== null && plan !== null && !blocked : true);

  async function save() {
    if (!canSave) return;
    setBusy(true); setError(null);
    try {
      if (isNew) {
        const r = await addGlossaryTerm(inst, { term: t, memo: m === '' ? null : m, enabled, place: place!, dry_run: false });
        onSaved(r as GlossaryView, `「${t}」 용어를 ${place === 'top' ? '맨 위' : '맨 아래'}에 넣었습니다.`);
      } else {
        const v = await patchGlossaryTerm(inst, term.id, { term: t, memo: m === '' ? null : m, enabled });
        onSaved(v, `「${t}」 용어를 고쳤습니다.`);
      }
    } catch (e: unknown) {
      setError(errText(e, isNew ? '용어를 넣지 못했습니다' : '용어를 고치지 못했습니다'));
      setBusy(false);
    }
  }

  const title = isNew ? '용어 추가' : '용어 고치기';
  return (
    <div className="cl-modal__backdrop" role="presentation" onMouseDown={onClose}>
      <div className="cl-modal gl-modal" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}>
        <div className="cl-modal__head">
          <h3>{title}</h3>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="cl-modal__body">
          <div className="cl-field">
            <label htmlFor="gl-term">용어</label>
            <input id="gl-term" className="xa-input" value={text} maxLength={termMax + 10} onChange={(e) => setText(e.target.value)} autoFocus />
            <p className="xa-hint">받아 적을 때 이 철자로 잡습니다 · {termMax}자까지{t.length > termMax && <b className="xa-danger"> · 지금 {t.length}자</b>}</p>
          </div>
          <div className="cl-field">
            <label htmlFor="gl-memo">메모 <span className="gl-sub">(선택 · 관리자용 · 힌트에 안 들어감)</span></label>
            <input id="gl-memo" className="xa-input" value={memo} onChange={(e) => setMemo(e.target.value)} />
            {!memoOk && <p className="xa-hint xa-danger" role="alert">메모는 {memoMax}자까지입니다 · 지금 {m.length}자</p>}
          </div>
          {isNew && <PlaceRadio name="gl-place-one" value={place} onChange={setPlace} />}
          <label className="xa-check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            켜기
          </label>
          {isNew && (place === null
            ? <p className="xa-hint">붙일 자리를 고르면 몇 번에 들어가고 힌트에 드는지 미리 보여 줍니다.</p>
            : plan
              ? <Summary lines={addSummary(plan, enabled, maxTerms)} />
              : preview.busy && termOk ? <p className="xa-hint">미리 보는 중…</p> : null)}
          {preview.error && <p className="xa-hint xa-danger" role="alert">{preview.error}</p>}
          {error && <p className="xa-hint xa-danger" role="alert">{error}</p>}
        </div>
        <div className="cl-modal__foot">
          <button type="button" className="cl-modal__btn" onClick={onClose}>취소</button>
          <button type="button" className="cl-modal__btn cl-modal__btn--primary" disabled={!canSave} onClick={() => void save()}>
            {busy ? '저장 중…' : isNew ? '추가' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BulkModalProps {
  inst: string;
  termMax: number;
  maxTerms: number;
  onClose: () => void;
  onSaved: (view: GlossaryView, message: string) => void;
}

export function GlossaryBulkModal({ inst, termMax, maxTerms, onClose, onSaved }: BulkModalProps) {
  const [text, setText] = useState('');
  const [place, setPlace] = useState<GlossaryPlace | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasText = text.trim() !== '';
  const previewKey = hasText && place ? JSON.stringify([text, place]) : null;
  const preview = usePreview<GlossaryPlan>(previewKey, async () => (await addGlossaryBulk(inst, { text, place: place!, dry_run: true })).plan);
  const plan = preview.value;
  const canSave = !busy && place !== null && plan !== null && plan.added > 0 && !plan.over_limit;

  async function save() {
    if (!canSave) return;
    setBusy(true); setError(null);
    try {
      const r = await addGlossaryBulk(inst, { text, place: place!, dry_run: false });
      onSaved(r as GlossaryView, `용어 ${r.plan.added}개를 ${place === 'top' ? '맨 위' : '맨 아래'}에 넣었습니다.`);
    } catch (e: unknown) {
      setError(errText(e, '용어를 넣지 못했습니다'));
      setBusy(false);
    }
  }

  return (
    <div className="cl-modal__backdrop" role="presentation" onMouseDown={onClose}>
      <div className="cl-modal gl-modal gl-modal--wide" role="dialog" aria-modal="true" aria-label="용어 여러 개 넣기" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cl-modal__head">
          <h3>용어 여러 개 넣기</h3>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="cl-modal__body">
          <div className="cl-field">
            <label htmlFor="gl-bulk">한 줄에 하나 · 메모는 «용어 | 메모»</label>
            <textarea id="gl-bulk" className="xa-input gl-ta" rows={8} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
            <p className="xa-hint">용어는 {termMax}자까지 · 같은 용어(대소문자 무시)는 건너뜁니다 · 메모는 힌트에 안 들어갑니다</p>
          </div>
          <PlaceRadio name="gl-place-bulk" value={place} onChange={setPlace} />
          {hasText && (place === null
            ? <p className="xa-hint">붙일 자리를 고르면 들어가는 것 · 밀려나는 것을 미리 보여 줍니다.</p>
            : plan
              ? <Summary lines={bulkSummary(plan, maxTerms)} />
              : preview.busy ? <p className="xa-hint">미리 보는 중…</p> : null)}
          {preview.error && <p className="xa-hint xa-danger" role="alert">{preview.error}</p>}
          {error && <p className="xa-hint xa-danger" role="alert">{error}</p>}
        </div>
        <div className="cl-modal__foot">
          <button type="button" className="cl-modal__btn" onClick={onClose}>취소</button>
          <button type="button" className="cl-modal__btn cl-modal__btn--primary" disabled={!canSave} onClick={() => void save()}>
            {busy ? '넣는 중…' : plan && plan.added > 0 ? `${plan.added}개 넣기` : '넣기'}
          </button>
        </div>
      </div>
    </div>
  );
}
